import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import flowStationService, { 
  FlowStation, 
  CreateFlowStationData, 
  UpdateFlowStationData 
} from '../../services/flowStationService';

interface FlowStationState {
  flowStations: FlowStation[];
  locationFlowStations: FlowStation[]; // flow stations for the currently selected location
  currentFlowStation: FlowStation | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  updateLoading: boolean;
  deleteLoading: boolean;
}

const initialState: FlowStationState = {
  flowStations: [],
  locationFlowStations: [],
  currentFlowStation: null,
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
};

// Async thunks
export const createFlowStation = createAsyncThunk(
  'flowStation/create',
  async (data: CreateFlowStationData, { rejectWithValue }) => {
    try {
      const response = await flowStationService.createFlowStation(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create flow station');
    }
  }
);

export const fetchFlowStations = createAsyncThunk(
  'flowStation/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await flowStationService.getAllFlowStations();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch flow stations');
    }
  }
);

export const fetchFlowStationById = createAsyncThunk(
  'flowStation/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await flowStationService.getFlowStationById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch flow station');
    }
  }
);

export const updateFlowStation = createAsyncThunk(
  'flowStation/update',
  async ({ id, data }: { id: string; data: UpdateFlowStationData }, { rejectWithValue }) => {
    try {
      const response = await flowStationService.updateFlowStation(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update flow station');
    }
  }
);

export const deleteFlowStation = createAsyncThunk(
  'flowStation/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await flowStationService.deleteFlowStation(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete flow station');
    }
  }
);

export const fetchFlowStationsByOwner = createAsyncThunk(
  'flowStation/fetchByOwner',
  async (ownerId: string, { rejectWithValue }) => {
    try {
      const response = await flowStationService.getFlowStationsByOwner(ownerId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch flow stations');
    }
  }
);

export const fetchFlowStationsByLocation = createAsyncThunk(
  'flowStation/fetchByLocation',
  async (locationId: string, { rejectWithValue }) => {
    try {
      const response = await flowStationService.getFlowStationsByLocation(locationId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch flow stations by location');
    }
  }
);

const flowStationSlice = createSlice({
  name: 'flowStation',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentFlowStation: (state) => {
      state.currentFlowStation = null;
    },
    setCurrentFlowStation: (state, action: PayloadAction<FlowStation>) => {
      state.currentFlowStation = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Flow Station
      .addCase(createFlowStation.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createFlowStation.fulfilled, (state, action) => {
        state.createLoading = false;
        state.flowStations.push(action.payload);
      })
      .addCase(createFlowStation.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload as string;
      })

      // Fetch All Flow Stations
      .addCase(fetchFlowStations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlowStations.fulfilled, (state, action) => {
        state.loading = false;
        state.flowStations = action.payload;
      })
      .addCase(fetchFlowStations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Flow Station by ID
      .addCase(fetchFlowStationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlowStationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentFlowStation = action.payload;
      })
      .addCase(fetchFlowStationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Update Flow Station
      .addCase(updateFlowStation.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateFlowStation.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.flowStations.findIndex(fs => fs._id === action.payload._id);
        if (index !== -1) {
          state.flowStations[index] = action.payload;
        }
        if (state.currentFlowStation?._id === action.payload._id) {
          state.currentFlowStation = action.payload;
        }
      })
      .addCase(updateFlowStation.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload as string;
      })

      // Delete Flow Station
      .addCase(deleteFlowStation.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteFlowStation.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.flowStations = state.flowStations.filter(fs => fs._id !== action.payload);
        if (state.currentFlowStation?._id === action.payload) {
          state.currentFlowStation = null;
        }
      })
      .addCase(deleteFlowStation.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload as string;
      })

      // Fetch Flow Stations by Owner
      .addCase(fetchFlowStationsByOwner.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlowStationsByOwner.fulfilled, (state, action) => {
        state.loading = false;
        state.flowStations = action.payload;
      })
      .addCase(fetchFlowStationsByOwner.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch Flow Stations by Location
      .addCase(fetchFlowStationsByLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.locationFlowStations = [];
      })
      .addCase(fetchFlowStationsByLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.locationFlowStations = action.payload;
      })
      .addCase(fetchFlowStationsByLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearCurrentFlowStation, setCurrentFlowStation } = flowStationSlice.actions;

export default flowStationSlice.reducer;